import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { CRM_CHANNELS, CRM_STATUS, CRM_HEAT } from "../../lib/constants";
import { enumOpts } from "../../lib/crmLabels";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { PhoneCall, Flame, CalendarDays, Mail, Phone, AlertTriangle } from "lucide-react";

function HeatDot({ k }) {
  const d = CRM_HEAT[k] || CRM_HEAT.tiede;
  return <span className="h-2 w-2 rounded-full inline-block" style={{ background: d.dot }} title={d.label} />;
}

function Row({ item, onRelance, onEmail, t, readOnly }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 p-3 bg-white/[0.02]" data-testid={`crm-relance-row-${item.id}`}>
      <div className="min-w-0">
        <div className="font-medium flex items-center gap-2"><HeatDot k={item.heat} /> {item.name}</div>
        <div className="text-xs text-muted-foreground truncate flex items-center gap-2 mt-0.5">
          {item.contact_name && <span>{item.contact_name}</span>}
          {item.contact_phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{item.contact_phone}</span>}
          {item.next_followup && <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{item.next_followup}</span>}
        </div>
      </div>
      {!readOnly && (
        <div className="flex items-center gap-1.5 shrink-0">
          <Button size="sm" variant="outline" onClick={() => onEmail(item)} data-testid={`crm-relance-email-btn-${item.id}`}>
            <Mail className="h-3.5 w-3.5 mr-1" /> Envoyer l'alerte par e-mail
          </Button>
          <Button size="sm" variant="outline" onClick={() => onRelance(item)} data-testid={`crm-relance-btn-${item.id}`}>
            <PhoneCall className="h-3.5 w-3.5 mr-1" /> {t("crm.relance.relanceBtn")}
          </Button>
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, color, items, onRelance, onEmail, t, readOnly }) {
  return (
    <div className="rounded-2xl border border-white/10 p-4 bg-white/[0.02]">
      <div className="flex items-center gap-2 mb-3 text-sm font-semibold" style={{ color }}>
        <Icon className="h-4 w-4" /> {title} <span className="text-foreground/40">({items.length})</span>
      </div>
      <div className="space-y-2">
        {items.map((i) => <Row key={i.id} item={i} onRelance={onRelance} onEmail={onEmail} t={t} readOnly={readOnly} />)}
        {!items.length && <p className="text-xs text-muted-foreground">{t("crm.relance.nothing")}</p>}
      </div>
    </div>
  );
}

const emptyFu = { prospect_id: "", prospect_name: "", channel: "tel", outcome: "", notes: "", new_status: "", next_followup: "" };

export default function RelancesTab({ onChange, readOnly }) {
  const { t } = useTranslation();
  const [data, setData] = useState({ due: [], hot: [], upcoming: [] });
  const [open, setOpen] = useState(false);
  const [fu, setFu] = useState(emptyFu);
  const [sending, setSending] = useState(false);
  const [emDlg, setEmDlg] = useState(false);
  const [emTarget, setEmTarget] = useState(null);
  const [emMsg, setEmMsg] = useState("");
  const [sendingEm, setSendingEm] = useState(false);
  const [relTemplates, setRelTemplates] = useState([]);

  const load = async () => {
    try { const r = await api.get("/crm/relances"); setData(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadTpl = async () => {
    try { const t = await api.get("/crm/email-templates?kind=relance"); setRelTemplates(t.data || []); } catch (e) { /* silencieux */ }
  };
  useEffect(() => { load(); loadTpl(); }, []);

  const saveRelTemplate = async () => {
    if (!emMsg.trim()) return toast.error("Écrivez d'abord un message.");
    const label = window.prompt("Nom du modèle de relance :");
    if (!label) return;
    try { await api.post("/crm/email-templates", { kind: "relance", label, text: emMsg }); toast.success("Modèle enregistré."); loadTpl(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openRelance = (item) => {
    setFu({ ...emptyFu, prospect_id: item.id, prospect_name: item.name, new_status: item.status, next_followup: item.next_followup || "" });
    setOpen(true);
  };
  const save = async () => {
    try {
      await api.post("/crm/followups", fu);
      toast.success(t("crm.relance.saved")); setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendAlert = async () => {
    setSending(true);
    try {
      const r = await api.post("/crm/relances/email", {});
      if (r.data.no_recipient) {
        toast.info(t("crm.relance.noRecipient"));
      } else {
        const s = (r.data.sent || []).filter((x) => typeof x === "string").length;
        toast.success(t("crm.relance.alertSent", { due: r.data.due, hot: r.data.hot, n: s }));
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const openEmail = (item) => { setEmTarget(item); setEmMsg(""); setEmDlg(true); };
  const sendEmailToContact = async () => {
    setSendingEm(true);
    try {
      const r = await api.post(`/crm/relances/${emTarget.id}/email`, { message: emMsg });
      toast.success(`Alerte envoyée à ${r.data.to}.`); setEmDlg(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSendingEm(false); }
  };

  return (
    <div className="space-y-4" data-testid="crm-relances-tab">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><PhoneCall className="h-4 w-4 text-rose-400" /> {t("crm.relance.title")}</h3>
        {!readOnly && <Button onClick={sendAlert} disabled={sending} className="bg-emerald-600 hover:bg-emerald-500" data-testid="crm-send-relance-alert">
          <Mail className="h-4 w-4 mr-2" /> {sending ? t("crm.relance.sending") : t("crm.relance.sendAlert")}
        </Button>}
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Section icon={AlertTriangle} title={t("crm.relance.due")} color="#f87171" items={data.due} onRelance={openRelance} onEmail={openEmail} t={t} readOnly={readOnly} />
        <Section icon={Flame} title={t("crm.relance.hot")} color="#fb923c" items={data.hot} onRelance={openRelance} onEmail={openEmail} t={t} readOnly={readOnly} />
        <Section icon={CalendarDays} title={t("crm.relance.upcoming")} color="#60a5fa" items={data.upcoming} onRelance={openRelance} onEmail={openEmail} t={t} readOnly={readOnly} />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("crm.relance.dialogTitle", { name: fu.prospect_name })}</DialogTitle>
            <DialogDescription className="sr-only">{t("crm.relance.title")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("crm.relance.channel")}</Label>
                <Select value={fu.channel} onValueChange={(v) => setFu({ ...fu, channel: v })}>
                  <SelectTrigger data-testid="crm-fu-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>{enumOpts(t, "channels", CRM_CHANNELS).map((o) => <SelectItem key={o.k} value={o.k}>{o.v}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t("crm.relance.newStatus")}</Label>
                <Select value={fu.new_status || "none"} onValueChange={(v) => setFu({ ...fu, new_status: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="crm-fu-status"><SelectValue placeholder={t("crm.relance.unchanged")} /></SelectTrigger>
                  <SelectContent><SelectItem value="none">{t("crm.relance.unchanged")}</SelectItem>{enumOpts(t, "status", CRM_STATUS).map((o) => <SelectItem key={o.k} value={o.k}>{o.v}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t("crm.relance.outcome")}</Label><Input value={fu.outcome} onChange={(e) => setFu({ ...fu, outcome: e.target.value })} placeholder={t("crm.relance.outcomePlaceholder")} /></div>
              <div className="space-y-1.5"><Label>{t("crm.relance.nextFollowup")}</Label><Input type="date" value={fu.next_followup || ""} onChange={(e) => setFu({ ...fu, next_followup: e.target.value })} data-testid="crm-fu-next" /></div>
            </div>
            <div className="space-y-1.5"><Label>{t("crm.relance.notes")}</Label><Textarea value={fu.notes} onChange={(e) => setFu({ ...fu, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("crm.actions.cancel")}</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-500" data-testid="crm-save-followup">{t("crm.actions.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emDlg} onOpenChange={setEmDlg}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4 text-rose-400" /> Envoyer une alerte par e-mail</DialogTitle>
            <DialogDescription>Un e-mail (no-reply) sera envoyé au contact « {emTarget?.name} » concernant sa demande.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>Message (optionnel)</Label>
                <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px] gap-1 text-sky-300" onClick={saveRelTemplate} data-testid="relance-save-template">+ Enregistrer comme modèle</Button>
              </div>
              {relTemplates.length > 0 && (
                <Select value="" onValueChange={(id) => { const tp = relTemplates.find((x) => x.id === id); if (tp) setEmMsg(tp.text); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="relance-template-picker"><SelectValue placeholder="Insérer un modèle enregistré…" /></SelectTrigger>
                  <SelectContent>{relTemplates.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.label}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Textarea rows={5} value={emMsg} onChange={(e) => setEmMsg(e.target.value)} placeholder="Ex. Nous souhaitons faire le point sur votre demande d'admission. N'hésitez pas à nous recontacter…" data-testid="relance-email-msg" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmDlg(false)}>{t("crm.actions.cancel")}</Button>
            <Button onClick={sendEmailToContact} disabled={sendingEm} className="bg-rose-600 hover:bg-rose-500 gap-1.5" data-testid="relance-email-send"><Mail className="h-4 w-4" /> {sendingEm ? "Envoi…" : "Envoyer l'alerte"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
